/* Custom PDP Hotspots
   State machine for Eight Sleep-style feature callouts.
   - One modal per section instance (lives inside the section)
   - Click a dot → open modal at that hotspot index, zoom-to-dot on image pane
   - Arrows / ← → cycle through hotspots, active dot syncs colour
   - ESC / overlay / X → close; native <dialog> handles focus trap
   - Teardown on shopify:section:unload (Theme Editor stability) */

(function () {
  'use strict';

  // Register a no-op Custom Element wrapper so CSS selectors work on both
  // the Liquid output and anything that later migrates to shadow/light DOM.
  if (typeof customElements !== 'undefined' && !customElements.get('bettr-hotspot-modal')) {
    customElements.define('bettr-hotspot-modal', class extends HTMLElement {});
  }

  var ZOOM = 2.5;       // zoom factor applied to the modal image on open
  var TRANSITION = 220; // ms — matches CSS opacity transition on dialog

  function initSection(section) {
    if (!section || section.dataset.hotspotsInit === '1') return;
    section.dataset.hotspotsInit = '1';

    var sectionId = section.dataset.sectionId;
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-hotspot-dot]'));
    if (dots.length === 0) return;

    // Modal lives in the same DOM subtree rendered by this section's Liquid,
    // BUT is a sibling of the <section> element (rendered directly inside the
    // {% section %} wrapper). Resolve it by section id rather than by relative
    // DOM traversal so we don't depend on Shopify's wrapper markup.
    var modal = document.getElementById('HotspotModal-' + (sectionId || '').replace(/_/g, '-'));
    if (!modal) {
      // Fallback: first <dialog data-hotspot-modal> that follows this section
      var next = section.parentNode;
      modal = next ? next.querySelector('[data-hotspot-modal]') : null;
    }
    if (!modal) return;

    var modalImage = modal.querySelector('[data-hotspot-modal-image]');
    var modalClose = modal.querySelector('[data-hotspot-modal-close]');
    var modalOverlay = modal.querySelector('[data-hotspot-modal-overlay]');
    var modalPrev = modal.querySelector('[data-hotspot-modal-prev]');
    var modalNext = modal.querySelector('[data-hotspot-modal-next]');
    var modalCounter = modal.querySelector('[data-hotspot-modal-counter]');
    var contentStack = modal.querySelectorAll('[data-hotspot-modal-content]');

    if (contentStack.length !== dots.length) {
      // Schema / JSON mismatch — fail quietly rather than half-init.
      console.warn('[hotspots] dot count vs. content stack mismatch');
    }

    var canvasImage = section.querySelector('[data-hotspots-image]');
    var canvasEl = section.querySelector('[data-hotspots-canvas]');
    var headerEl = section.querySelector('.custom-pdp-hs__header');

    var currentIndex = -1;
    var lastFocused = null;
    var teardown = [];
    // Picture coordinates (% of the source image) each dot resolved to on
    // the last positionDots() run; the modal zoom reads them too.
    var coords = [];

    // Band geometry for the crop fit (E17, 30 Oct 2026). A dot's hit target
    // is 44 px; its centre keeps HALF + GAP from the band's edges and from
    // the heading/line copy when that copy sits on the picture.
    var DOT_HALF = 22;
    var GAP = 8;

    // Per-picture positions. One template serves all four Ritual pages and
    // each page shows its own render (product metafield
    // custom.hotspots_asset), with the cabin at a different place in each,
    // so one X / Y per block cannot land on the same part in all four. A
    // block's "Position per picture" lines — "<part of the picture's file
    // name> <x> <y>", e.g. "ritual-2-person-v2 38 71.3" — win for the
    // picture whose file name contains that part; dot_x / dot_y are the
    // fallback for any other picture.
    function pictureCoords(dot, src) {
      var x = parseFloat(dot.dataset.dotX);
      var y = parseFloat(dot.dataset.dotY);
      var spec = dot.getAttribute('data-dot-positions') || '';
      var file = (src || '').split('?')[0].split('/').pop();
      if (spec && file) {
        var lines = spec.split(/[\n;]+/);
        for (var i = 0; i < lines.length; i++) {
          var p = lines[i].trim().split(/[\s,\/:]+/);
          if (p.length < 3 || !p[0] || file.indexOf(p[0]) === -1) continue;
          var px = parseFloat(p[1]);
          var py = parseFloat(p[2]);
          if (!isNaN(px) && !isNaN(py)) { x = px; y = py; break; }
        }
      }
      return { x: x, y: y };
    }

    // object-position token → 0..1 ("center 60%", "50% 50%", "center top").
    function parseOp(token, fallback) {
      if (!token) return fallback;
      if (token === 'center') return 0.5;
      if (token === 'left' || token === 'top') return 0;
      if (token === 'right' || token === 'bottom') return 1;
      var m = token.match(/^(-?\d+(?:\.\d+)?)%$/);
      if (m) return parseFloat(m[1]) / 100;
      return fallback;
    }

    // Offset (px, between minOff and 0) of the picture's top or left edge
    // that keeps every dot centre pos[i] + offset within [lo[i], hi]: the
    // CSS framing (pref) when it already does, else the nearest offset that
    // does. null when no offset satisfies them all.
    function fitOffset(pref, minOff, pos, lo, hi) {
      var a = minOff, b = 0;
      for (var i = 0; i < pos.length; i++) {
        if (isNaN(pos[i])) continue;
        a = Math.max(a, lo[i] - pos[i]);
        b = Math.min(b, hi - pos[i]);
      }
      if (a > b) return null;
      return Math.min(Math.max(pref, a), b);
    }

    // The heading/line box relative to the canvas, when it sits on the
    // picture (bleed layout); null when it sits above it (contained card).
    function copyBox(cRect) {
      if (!headerEl) return null;
      var h = headerEl.getBoundingClientRect();
      if (!h.width || !h.height || h.bottom <= cRect.top || h.top >= cRect.bottom) return null;
      return { left: h.left - cRect.left, right: h.right - cRect.left, bottom: h.bottom - cRect.top };
    }

    // Cover-aware dot positioning. A dot's coordinates are % of the SOURCE
    // IMAGE (not the canvas). object-fit: cover scales the picture to fill
    // the canvas and clips the overflow on one axis; this places each dot on
    // the canvas pixel that shows its picture pixel, at any canvas aspect,
    // breakpoint or object-position.
    //
    // CSS owns the framing (object-position: the contained card's
    // "center 60%", the bleed band's --hs-bleed-y). When that framing would
    // put a dot's target outside the band, or under the heading/line copy,
    // the crop moves by the least that brings every dot in, and the dots
    // follow (E17: the 2-Person's base sits at 84–89% of its picture, below
    // the band's 60% crop).
    function positionDots() {
      if (!canvasImage || !canvasEl) return;
      var iw = canvasImage.naturalWidth;
      var ih = canvasImage.naturalHeight;
      if (!iw || !ih) return;
      var cRect = canvasEl.getBoundingClientRect();
      var cw = cRect.width, ch = cRect.height;
      if (!cw || !ch) return;
      // Read the CSS framing, not an earlier fit.
      canvasImage.style.objectPosition = '';
      var parts = (getComputedStyle(canvasImage).objectPosition || '50% 50%').trim().split(/\s+/);
      var opX = parseOp(parts[0], 0.5);
      var opY = parseOp(parts[1], 0.5);
      // cover: scale by the LARGER of (cw/iw, ch/ih); the other axis overflows.
      var scale = Math.max(cw / iw, ch / ih);
      var renderedW = iw * scale;
      var renderedH = ih * scale;
      var overX = renderedW - cw;
      var overY = renderedH - ch;
      var src = canvasImage.currentSrc || canvasImage.src || '';
      coords = dots.map(function (dot) { return pictureCoords(dot, src); });
      var xs = coords.map(function (c) { return c.x / 100 * renderedW; });
      var ys = coords.map(function (c) { return c.y / 100 * renderedH; });
      var edge = DOT_HALF + GAP;
      // offsetX / offsetY: the picture's left / top edge in the canvas.
      var offsetX = -overX * opX;
      var offsetY = -overY * opY;
      if (overX > 0.5) {
        var fx = fitOffset(offsetX, -overX, xs, xs.map(function () { return edge; }), cw - edge);
        if (fx !== null) offsetX = fx;
      }
      if (overY > 0.5) {
        var copy = copyBox(cRect);
        var lo = xs.map(function (x) {
          var sx = offsetX + x;
          var underCopy = copy && sx + edge > copy.left && sx - edge < copy.right;
          return underCopy ? copy.bottom + edge : edge;
        });
        var fy = fitOffset(offsetY, -overY, ys, lo, ch - edge);
        // Copy and band edges can't both be cleared: keep the dots in the band.
        if (fy === null) fy = fitOffset(offsetY, -overY, ys, ys.map(function () { return edge; }), ch - edge);
        if (fy !== null) offsetY = fy;
      }
      if (Math.abs(offsetX + overX * opX) > 0.5 || Math.abs(offsetY + overY * opY) > 0.5) {
        var posX = overX > 0.5 ? -offsetX / overX * 100 : opX * 100;
        var posY = overY > 0.5 ? -offsetY / overY * 100 : opY * 100;
        canvasImage.style.objectPosition = posX.toFixed(3) + '% ' + posY.toFixed(3) + '%';
      }
      dots.forEach(function (dot, i) {
        if (isNaN(xs[i]) || isNaN(ys[i])) return;
        dot.style.left = ((offsetX + xs[i]) / cw * 100) + '%';
        dot.style.top = ((offsetY + ys[i]) / ch * 100) + '%';
      });
    }

    function on(target, event, handler, opts) {
      target.addEventListener(event, handler, opts);
      teardown.push(function () { target.removeEventListener(event, handler, opts); });
    }

    function applyZoom(index) {
      var dot = dots[index];
      if (!dot || !modalImage) return;
      var c = coords[index] || pictureCoords(dot, canvasImage ? (canvasImage.currentSrc || canvasImage.src) : '');
      var x = c.x;
      var y = c.y;
      if (isNaN(x) || isNaN(y)) {
        modalImage.style.transform = 'scale(1)';
        return;
      }
      // The phone modal's picture is object-fit: cover in a box that is not
      // square (full width × 280), so % of the box would miss the part
      // vertically. Put the dot's picture pixel in the box's centre, in px,
      // and keep the zoomed picture covering the box.
      var ew = modalImage.offsetWidth;
      var eh = modalImage.offsetHeight;
      var iw = modalImage.naturalWidth || (canvasImage && canvasImage.naturalWidth);
      var ih = modalImage.naturalHeight || (canvasImage && canvasImage.naturalHeight);
      if (ew && eh && iw && ih) {
        var parts = (getComputedStyle(modalImage).objectPosition || '50% 50%').trim().split(/\s+/);
        var s = Math.max(ew / iw, eh / ih);
        var rw = iw * s, rh = ih * s;
        var ox = (ew - rw) * parseOp(parts[0], 0.5);
        var oy = (eh - rh) * parseOp(parts[1], 0.5);
        var cx = ew / 2, cy = eh / 2;
        var pxT = -ZOOM * (ox + x / 100 * rw - cx);
        var pyT = -ZOOM * (oy + y / 100 * rh - cy);
        pxT = Math.min(Math.max(pxT, ew - cx - ZOOM * (ox + rw - cx)), -(cx + ZOOM * (ox - cx)));
        pyT = Math.min(Math.max(pyT, eh - cy - ZOOM * (oy + rh - cy)), -(cy + ZOOM * (oy - cy)));
        modalImage.style.transform = 'translate(' + pxT.toFixed(1) + 'px, ' + pyT.toFixed(1) + 'px) scale(' + ZOOM + ')';
        return;
      }
      // No layout yet (dialog closed, or the desktop panel hides the media).
      var tx = (50 - x) * ZOOM;
      var ty = (50 - y) * ZOOM;
      modalImage.style.transform = 'translate(' + tx + '%, ' + ty + '%) scale(' + ZOOM + ')';
    }

    function showHotspot(index) {
      if (index < 0 || index >= dots.length) return;

      // Swap visible content pane
      for (var i = 0; i < contentStack.length; i++) {
        contentStack[i].hidden = (i !== index);
      }

      // Update the dialog's aria-labelledby to point to the currently-visible
      // title so screen readers re-announce the dialog name on cycle. Each
      // .hotspot-modal__title carries a slot-specific id (HotspotContent-N-uid).
      var visibleTitle = modal.querySelector('.hotspot-modal__content:not([hidden]) .hotspot-modal__title');
      if (visibleTitle && visibleTitle.id) modal.setAttribute('aria-labelledby', visibleTitle.id);

      // Sync dot aria-expanded
      for (var j = 0; j < dots.length; j++) {
        dots[j].setAttribute('aria-expanded', j === index ? 'true' : 'false');
      }

      // Zoom the modal image
      applyZoom(index);

      // Counter text
      if (modalCounter) {
        modalCounter.textContent = (index + 1) + ' / ' + dots.length;
      }

      currentIndex = index;
    }

    function openModal(index) {
      lastFocused = document.activeElement;
      showHotspot(index);

      if (typeof modal.showModal === 'function') {
        try { modal.showModal(); }
        catch (e) { modal.setAttribute('open', ''); }
      } else {
        modal.setAttribute('open', '');
      }

      // The media box has a size only now that the dialog is open: zoom
      // again from its real geometry, without animating from the guess.
      if (modalImage) {
        modalImage.style.transition = 'none';
        applyZoom(index);
        void modalImage.offsetWidth;
        modalImage.style.transition = '';
      }

      // Defer one tick so the dialog's [open] paint settles before we toggle
      // is-open — gives the panel transform + opacity transitions a clean
      // "from" state. setTimeout(0) is used here instead of rAF because rAF
      // callbacks queued immediately after showModal() can be starved in some
      // Chromium builds (the dialog enters the top layer mid-frame and the
      // rAF callback is dropped). setTimeout(0) survives that hand-off.
      setTimeout(function () { modal.classList.add('is-open'); }, 0);

      if (window.BettrMetrics && typeof window.BettrMetrics.mark === 'function') {
        window.BettrMetrics.mark('hotspot-modal-open');
      }
    }

    function closeModal() {
      modal.classList.remove('is-open');

      // Wait for fade, then actually close + reset state
      setTimeout(function () {
        if (typeof modal.close === 'function') {
          try { modal.close(); } catch (e) { modal.removeAttribute('open'); }
        } else {
          modal.removeAttribute('open');
        }
        for (var i = 0; i < dots.length; i++) {
          dots[i].setAttribute('aria-expanded', 'false');
        }
        if (modalImage) modalImage.style.transform = 'scale(1)';
        currentIndex = -1;
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
        }
      }, TRANSITION);
    }

    function cycle(delta) {
      if (currentIndex < 0) return;
      var next = (currentIndex + delta + dots.length) % dots.length;
      showHotspot(next);
      // Refocus the new active dot's aria-owner so screen readers announce.
      if (modalCounter) {
        // aria-live="polite" on counter already announces; no extra focus move.
      }
    }

    // Dot click handlers
    dots.forEach(function (dot, i) {
      on(dot, 'click', function (e) {
        e.preventDefault();
        openModal(i);
      });
    });

    // Modal control handlers
    if (modalClose) on(modalClose, 'click', closeModal);
    if (modalOverlay) on(modalOverlay, 'click', closeModal);
    if (modalPrev) on(modalPrev, 'click', function () { cycle(-1); });
    if (modalNext) on(modalNext, 'click', function () { cycle(1); });

    // Native <dialog> cancel event (fires on Esc). Prevent default so we can
    // run our fade-out class-removal sequence cleanly.
    on(modal, 'cancel', function (e) {
      e.preventDefault();
      closeModal();
    });

    // Keyboard arrow navigation when modal is open
    on(document, 'keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); cycle(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); cycle(1); }
    });

    // Dot positioning — run on image load, on resize (debounced via rAF),
    // and once inline in case the image is already cached on nav.
    if (canvasImage) {
      if (canvasImage.complete && canvasImage.naturalWidth) {
        positionDots();
      } else {
        on(canvasImage, 'load', positionDots);
      }
    }
    var resizeRaf = 0;
    function schedulePosition() {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        resizeRaf = 0;
        positionDots();
      });
    }
    on(window, 'resize', schedulePosition);
    // The band's height and the copy's height change without a window
    // resize too (web font swap, scrollbar, Theme Editor): re-fit then.
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(schedulePosition);
      if (canvasEl) ro.observe(canvasEl);
      if (headerEl) ro.observe(headerEl);
      teardown.push(function () { ro.disconnect(); });
    }
    // The section sits under `content-visibility: auto` (theme.liquid,
    // 2026-07-11): while skip-rendered, the image has no geometry, so a
    // load-time positionDots() computes zeros. Re-run when the browser
    // un-skips the section as it scrolls near. No-op where unsupported —
    // those browsers don't skip rendering either.
    on(section, 'contentvisibilityautostatechange', function (e) {
      if (e.skipped === false) positionDots();
    });

    // Theme Editor stability — tear down listeners + internal state
    on(document, 'shopify:section:unload', function (e) {
      if (!e.target || !e.target.contains || !e.target.contains(section)) return;
      teardown.forEach(function (fn) { try { fn(); } catch (err) {} });
      teardown.length = 0;
      section.dataset.hotspotsInit = '0';
    });
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-hotspots-section]');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Handle Theme Editor inserting a new section instance at runtime
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target && e.target.querySelector
      ? e.target.querySelector('[data-hotspots-section]')
      : null;
    if (s) initSection(s);
  });
})();
